/// <amd-module name="Release/Landing/HubContent/Deployments/DeploymentsDetailsList" />
import * as React from "react";

import { VssComponent, IVssComponentProperties } from "VSS/Platform/Layout";
import { getIdentityImageUrl } from "VSS/Platform/Identity";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { ObservableValue } from "VSS/Core/Observable";

import { Status, StatusSize } from "VSSUI/Status";
import { Tooltip } from "VSSUI/TooltipEx";
import { Icon } from "VSSUI/Icon";
import { Image } from "VSSUI/Image";
import { Table, ITableColumn, TableCell } from "VSSUI/Table";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";

import { Deployment } from "Release/Client/Contracts/Release";
import { getComputedStatus, getStatusInfo } from "Release/EnvironmentNode/StatusComputer";
import { getIconStatusProps } from "Release/EnvironmentNode/Helpers";
import { createReleaseViewUrl, createReleaseLogsUrl } from "Release/Helpers/Link";
import { getArtifactVersionUrl, getPrimaryArtifact, getArtifactBranchName, getArtifactBuildInfo, getBranchDisplayValue } from "Artifact";
import * as Resources from "Release/Landing/Resources";

export interface IDeploymentsListProps extends IVssComponentProperties {
    deployments: Deployment[];
}

export class DeploymentsDetailsList extends VssComponent<IDeploymentsListProps, {}>{
    public render(): React.ReactNode {
        return (<Table
            className={"deployments-details-list dep-table"}
            itemProvider={new ArrayItemProvider(this.props.deployments)}
            columns={this._getTableColumns()}
            showHeader={true}
            showLines={false}
            pageSize={30}
        />);
    }

    private _renderTableCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<Deployment>, tableItem: Deployment): JSX.Element => {
        return (
            <TableCell key={"table-cell-" + tableItem.id + "-col-" + tableColumn.id}
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                className={"dep-table-cell"}
            >
                {this._renderItemColumnWithKey(tableItem, tableColumn.id)}
            </TableCell>);
    }

    private _renderItemColumnWithKey = (deployment: Deployment, columnKey: string): React.ReactNode => {
        const primaryArtifact = getPrimaryArtifact(deployment.release.artifacts);

        switch (columnKey) {
            case environmentColumnKey:
                const computedStatus = getComputedStatus(deployment.deploymentStatus, deployment.operationStatus);
                const statusIndicator = getStatusInfo(computedStatus).statusIndicator;
                const statusProps = getIconStatusProps(statusIndicator);

                return (<div className={"deployments-stage-column full-size"}>
                    <Status className={"deployment-status-icon"} {...statusProps} size={StatusSize.s} />
                    <Tooltip text={deployment.releaseEnvironment.name} overflowOnly={true}>
                        <FPSLink
                            rel={"noopener noreferrer"}
                            href={createReleaseLogsUrl(this.context.pageContext, deployment.release.id, deployment.releaseEnvironment.id)}
                            telemetrySource={"deployments-list-stage-link"}
                            className={"deployments-list-stage-link overflow-ellipsis"}
                        >
                            {deployment.releaseEnvironment.name}
                        </FPSLink>
                    </Tooltip>
                </div>);

            case releaseColumnKey:

                return (
                    <div className={"full-size"}>
                        <Tooltip text={deployment.release.name} overflowOnly={true}>
                            <FPSLink
                                rel={"noopener noreferrer"}
                                href={createReleaseViewUrl(this.context.pageContext, deployment.release.id)}
                                telemetrySource={"deployments-list-release-link"}
                                className={"deployments-list-release-link overflow-ellipsis"}
                            >
                                {deployment.release.name}
                            </FPSLink>
                        </Tooltip>
                    </div>);

            case artifactVersionColumnKey:
                if (!primaryArtifact) {
                    return null;
                }

                const url = getArtifactVersionUrl(this.context.pageContext, primaryArtifact);
                const version = getArtifactBuildInfo(primaryArtifact);

                if (!version) {
                    return null;
                }

                let content: React.ReactNode = "";
                if (url) {
                    content = (
                        <FPSLink
                            rel={"noopener noreferrer"}
                            href={url}
                            telemetrySource={"deployments-list-artifact-link"}
                            className={"deployments-list-artifact-link overflow-ellipsis"}
                        >
                            {version}
                        </FPSLink>);
                }
                else {
                    content = (<span className={"overflow-ellipsis"}>{version}</span>);
                }

                return (<div className={"full-size"}>
                    <Tooltip text={version} overflowOnly={true}>
                        {content}
                    </Tooltip>
                </div>);


            case branchColumnKey:
                if (!primaryArtifact) {
                    return null;
                }

                const branchName: string = getArtifactBranchName(primaryArtifact);
                const branchDisplayName: string = getBranchDisplayValue(branchName);

                return (<>
                    <Icon
                        iconName={"OpenSource"} />
                    {branchDisplayName}
                </>);

            case createdByColumnKey:
                return (<div className={"full-size"}>
                    <Image
                        className="release-deployment-identity-image"
                        src={getIdentityImageUrl(this.context.pageContext, deployment.requestedFor)}
                        alt={deployment.requestedFor.displayName}
                    />
                    <div className={"deployment-identity-image-name"}>{deployment.requestedFor.displayName}</div>
                </div>);

            case queuedTimeColumnKey:
                return deployment.queuedOn.toLocaleString();
        }
    }

    private _getTableColumns(): ITableColumn<Deployment>[] {
        const columnRefs = [
            {
                key: environmentColumnKey,
                name: Resources.DeploymentsEnvironmentColumnHeader,
            },
            {
                key: releaseColumnKey,
                name: Resources.DeploymentsReleaseColumnHeader,
            },
            {
                key: artifactVersionColumnKey,
                name: Resources.DeploymentsBuildColumnHeader,
            },
            {
                key: branchColumnKey,
                name: Resources.DeploymentsBranchColumnHeader,
            },
            {
                key: createdByColumnKey,
                name: Resources.DeploymentsDeployedByColumnHeader,
            },
            {
                key: queuedTimeColumnKey,
                name: Resources.DeploymentsTimeColumnHeader,
            }
        ];

        const tableColumns: ITableColumn<Deployment>[] = columnRefs.map(col => {
            let width = _colWidthMap[col.key];

            if (!width) {
                switch (col.key) {
                    case environmentColumnKey:
                    case releaseColumnKey:
                    case artifactVersionColumnKey:
                        width = 200; // -22; // Changing from %age to pixel to support resizing. I'll discuss with framework for more guidance
                        break;
                    case branchColumnKey:
                    case createdByColumnKey:
                        width = 200; //-17; // Changing from %age to pixel to support resizing. I'll discuss with framework for more guidance
                        break;
                    case queuedTimeColumnKey:
                        width = -100;
                        break;
                }

                _colWidthMap[col.key] = width;
            }

            return {
                id: col.key,
                name: col.name,
                headerClassName: "deployments-details-list-header",
                width: new ObservableValue(width),
                minWidth: 125,
                renderCell: this._renderTableCell,
                onSize: (event, index, newWidth: number) => {
                    _colWidthMap[col.key] = newWidth;
                    this.setState({});
                }
            } as ITableColumn<Deployment>;
        });

        return tableColumns;
    }
}

const _colWidthMap: { [col: string]: number } = {};

const environmentColumnKey = "deployments-list-environment-col";
const releaseColumnKey = "deployments-list-release-col";
const artifactVersionColumnKey = "deployments-list-artifact-col";
const branchColumnKey = "deployments-list-branch-col";
const createdByColumnKey = "deployments-list-createdby-col";
const queuedTimeColumnKey = "deployments-list-queued-time-col";
